import { defineMessages, useIntl } from 'react-intl';
import { string } from 'yup';

import { useSession } from '@hooks/useSession';

import {
  AccountType,
  PermissionScope,
  ResourceSourceType,
} from 'generated/types';

const messages = defineMessages({
  mustBeAtLeast_8Characters: {
    id: 'passwordRules.mustBeAtLeast_8Characters',
    defaultMessage: 'Must be at least 8 characters',
  },
  mustContainAtLeastOneUppercaseCharacter: {
    id: 'passwordRules.mustContainAtLeastOneUppercaseCharacter',
    defaultMessage: 'Must contain at least one uppercase character',
  },
  mustContainAtLeastOneLowercaseCharacter: {
    id: 'passwordRules.mustContainAtLeastOneLowercaseCharacter',
    defaultMessage: 'Must contain at least one lowercase character',
  },
  mustContainAtLeastOneNumber: {
    id: 'passwordRules.mustContainAtLeastOneNumber',
    defaultMessage: 'Must contain at least one number',
  },
  mustContainAtLeastOneSpecialCaseCharacter: {
    id: 'passwordRules.mustContainAtLeastOneSpecialCaseCharacter',
    defaultMessage: 'Must contain at least one special case character',
  },
  requiredText: {
    id: 'GLOBAL.requiredText',
    defaultMessage: 'Required',
  },
  invalidPhoneFormat: {
    id: 'GLOBAL.invalidPhoneFormat',
    defaultMessage:
      'Phone number is not valid. Try including a country code (e.g. +1)',
  },
  invalidEmail: {
    id: 'GLOBAL.invalidEmail',
    defaultMessage: 'Invalid email',
  },
  lowercase: {
    id: 'GLOBAL.lowercaseEmail',
    defaultMessage: 'Email must be lowercase',
  },
});

const useCommonSchemas = () => {
  const { formatMessage } = useIntl();
  const {
    state: { isCurator },
  } = useSession();

  const recordSourceSchema = isCurator
    ? string()
        .oneOf(Object.values(ResourceSourceType))
        .required(formatMessage(messages.requiredText))
    : string().oneOf(Object.values(ResourceSourceType));

  const escapeSpecialCharacters = (str: string) =>
    str.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');

  const passwordSchema = string()
    .min(8, formatMessage(messages.mustBeAtLeast_8Characters))
    .max(255)
    .required(formatMessage(messages.requiredText))
    .matches(
      /[A-Z]/u,
      formatMessage(messages.mustContainAtLeastOneUppercaseCharacter)
    )
    .matches(
      /[a-z]/u,
      formatMessage(messages.mustContainAtLeastOneLowercaseCharacter)
    )
    .matches(/\d/u, formatMessage(messages.mustContainAtLeastOneNumber))
    .matches(
      /[@$!%*#?&^()\-_=+[\]{}/\\,<>`~|]/u,
      formatMessage(messages.mustContainAtLeastOneSpecialCaseCharacter)
    );
  const phoneSchema = string().matches(/^\+[1-9]\d{1,14}$/, {
    message: formatMessage(messages.invalidPhoneFormat),
    excludeEmptyString: true,
  });
  const emailSchema = string()
    .email(formatMessage(messages.invalidEmail))
    .lowercase(formatMessage(messages.lowercase))
    .strict(true);

  const customStringSchema = (
    minLength: number,
    maxLength: number,
    allowedCharacters: string
  ) =>
    string()
      .min(minLength)
      .max(maxLength)
      .matches(
        new RegExp(
          `^[a-zA-Z0-9${escapeSpecialCharacters(
            allowedCharacters
          )}]{${minLength},${maxLength}}$`
        )
      );

  const nameSchema = (minLength = 1, maxLength = 64) =>
    customStringSchema(minLength, maxLength, `+=,.@-_`);
  const descriptionSchema = (minLength = 1, maxLength = 1000) =>
    customStringSchema(
      minLength,
      maxLength,
      `\t\n_+=,. @-/[]{}!#$%^&*():;"'<>\``
    );
  const AdminSchema = {
    name: nameSchema,
    description: descriptionSchema,
    permissionScope: string<PermissionScope>().matches(
      new RegExp(`^(${Object.values(PermissionScope).join('|')})$`)
    ),
    roleScope: string<AccountType>().matches(
      new RegExp(`^(${Object.values(AccountType).join('|')})$`)
    ),
  };

  return {
    emailSchema,
    passwordSchema,
    phoneSchema,
    recordSourceSchema,
    AdminSchema,
  };
};

export default useCommonSchemas;
